import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

const kpiCards = [
  {
    label: { nl: "OMZET", en: "REVENUE" },
    value: "148K",
    change: "+12%",
    line: "M0 36C12 29 19 31 28 24C37 16 45 28 54 18C63 8 72 17 84 4",
  },
  {
    label: { nl: "NIEUWE KLANTEN", en: "NEW CLIENTS" },
    value: "184",
    change: "+7%",
    line: "M0 34C13 28 19 32 29 24C39 16 48 24 59 15C68 7 75 13 84 6",
  },
  {
    label: "SERVICE",
    value: "99.8%",
    change: "+0.2%",
    kind: "uptime",
  },
];

const regionalBars = [50, 36, 43, 39, 58];
const analyticsFocusVisuals = ["definitions", "overview", "signals"];
const analyticsMethodVisuals = ["sources", "model", "alerts", "quality"];

const stripMediaSlotProps = (props) => {
  const nextProps = { ...props };
  ["src", "alt", "label", "hint", "aspect", "objectPosition"].forEach((key) => {
    delete nextProps[key];
  });
  return nextProps;
};

export function AnalyticsDashboardVisual({
  className,
  title = "Analytics dashboard visual",
  ...frameProps
}) {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const labels = {
    header: isNl ? "PRESTATIE COCKPIT" : "PERFORMANCE ANALYTICS",
    search: isNl ? "Zoeken" : "Search",
    monthlyGrowth: isNl ? "MAANDELIJKE GROEI" : "MONTHLY GROWTH",
    regionPerformance: isNl ? "REGIO PERFORMANCE" : "REGION PERFORMANCE",
    responseTime: isNl ? "REACTIETIJD" : "RESPONSE TIME",
    sources: isNl ? "BRONNEN" : "SOURCES",
    conversion: isNl ? "CONVERSIE" : "CONVERSION",
    alerts: isNl ? "SIGNALEN" : "ALERTS",
    margin: isNl ? "MARGE" : "MARGIN",
    forecast: "FORECAST",
    dataCheck: isNl ? "DATACHECK" : "DATA CHECK",
    cashflow: "CASHFLOW",
    phone: isNl ? "Tel" : "Phone",
  };
  const cards = kpiCards.map((card) => ({
    ...card,
    label: typeof card.label === "string" ? card.label : isNl ? card.label.nl : card.label.en,
  }));

  return (
    <AnalyticsHeroFrame
      {...stripMediaSlotProps(frameProps)}
      className={className}
      data-analytics-hero-visual
      role="img"
      aria-label={title}
    >
      <svg viewBox="0 0 920 720" focusable="false">
        <defs>
          <linearGradient id="analytics-card-fill" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.075)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.018)" />
          </linearGradient>
          <linearGradient id="analytics-line-gradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="rgba(205,235,243,0.28)" />
            <stop offset="0.46" stopColor="rgba(205,235,243,0.96)" />
            <stop offset="1" stopColor="rgba(123,210,236,0.72)" />
          </linearGradient>
          <linearGradient id="analytics-area-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(123,210,236,0.42)" />
            <stop offset="1" stopColor="rgba(123,210,236,0.015)" />
          </linearGradient>
        </defs>

        <rect className="analytics-shell" x="24" y="20" width="872" height="680" rx="38" />
        <path className="analytics-glass-line" d="M94 82H862" />
        <rect className="analytics-side-rail" x="24" y="20" width="54" height="680" rx="32" />
        <g data-analytics-rail-icons>
          <g className="analytics-rail-icon active" transform="translate(41 122)">
            <rect width="20" height="20" rx="5" />
            <path d="M6 6H9V9H6ZM12 6H15V9H12ZM6 12H9V15H6ZM12 12H15V15H12Z" />
          </g>
          <g className="analytics-rail-icon" transform="translate(41 174)">
            <rect width="20" height="20" rx="5" />
            <path d="M5 14L9 10L12 12L16 7" />
            <path d="M5 16H16" />
          </g>
          <g className="analytics-rail-icon" transform="translate(41 226)">
            <rect width="20" height="20" rx="5" />
            <path d="M10 5V10H15" />
            <path d="M16 10A6 6 0 1 1 10 4" />
          </g>
          <g className="analytics-rail-icon" transform="translate(41 278)">
            <rect width="20" height="20" rx="5" />
            <path d="M5 15V7M10 15V5M15 15V10" />
          </g>
          <g className="analytics-rail-icon" transform="translate(41 330)">
            <rect width="20" height="20" rx="5" />
            <path d="M10 5V15M7 8C7 6.5 13 6.5 13 8C13 10 7 9 7 12C7 13.5 13 13.5 13 12" />
          </g>
        </g>

        <text className="analytics-header-title" x="104" y="62">{labels.header}</text>
        <rect className="analytics-search" x="344" y="40" width="244" height="34" rx="11" />
        <circle className="analytics-search-dot" cx="363" cy="57" r="6" />
        <path className="analytics-search-handle" d="M367 61L372 66" />
        <text className="analytics-header-muted" x="390" y="62">{labels.search}</text>
        <circle className="analytics-user" cx="842" cy="57" r="13" />
        <circle className="analytics-user-glow" cx="842" cy="57" r="21" />

        <g data-analytics-kpi-row transform="translate(104 96)">
          {cards.map((card, index) => (
            <g key={card.label} data-analytics-kpi-card transform={`translate(${index * 244} 0)`}>
              <rect className="analytics-card" width="222" height="96" rx="17" />
              <text className="analytics-label" x="16" y="28">{card.label}</text>
              <text className={card.kind === "uptime" ? "analytics-value compact" : "analytics-value"} x="16" y="66">{card.value}</text>
              <text className="analytics-change" x={card.kind === "uptime" ? 168 : 174} y="28">{card.change}</text>
              {card.kind === "uptime" ? (
                <g data-analytics-uptime-mini transform="translate(120 34)">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((tick) => (
                    <rect
                      key={tick}
                      className={tick === 5 ? "analytics-uptime-bar downtime" : "analytics-uptime-bar"}
                      x={tick * 10}
                      y={tick === 5 ? 25 : 12}
                      width="6"
                      height={tick === 5 ? 15 : 28}
                      rx="3"
                    />
                  ))}
                  <path className="analytics-uptime-track" d="M0 26H78" />
                  <path className="analytics-uptime-live" d="M0 26H24V18H32V26H50V34H58V26H78" pathLength="100" />
                </g>
              ) : (
                <g transform="translate(121 39)">
                  <path className="analytics-mini-area" d={`${card.line}V46H0Z`} />
                  <path className="analytics-mini-line-base" d={card.line} pathLength="100" />
                  <path className="analytics-mini-line-live" d={card.line} pathLength="100" />
                </g>
              )}
            </g>
          ))}
        </g>

        <g data-analytics-main-chart transform="translate(104 214)">
          <rect className="analytics-card" width="506" height="240" rx="18" />
          <text className="analytics-label" x="18" y="30">{labels.monthlyGrowth}</text>
          <g className="analytics-chart-grid">
            {[0, 1, 2, 3, 4].map((line) => (
              <path key={`h-${line}`} d={`M36 ${67 + line * 34}H478`} />
            ))}
            {[0, 1, 2, 3, 4, 5, 6].map((line) => (
              <path key={`v-${line}`} d={`M36 ${55}V206`} transform={`translate(${line * 72} 0)`} />
            ))}
          </g>
          <path
            className="analytics-main-area"
            d="M38 198C68 144 88 164 118 150C150 135 152 104 185 113C218 122 204 168 238 156C270 143 282 97 322 111C360 124 367 83 404 88C437 92 437 128 478 72V206H38Z"
          />
          <path
            className="analytics-main-line-base"
            d="M38 198C68 144 88 164 118 150C150 135 152 104 185 113C218 122 204 168 238 156C270 143 282 97 322 111C360 124 367 83 404 88C437 92 437 128 478 72"
            pathLength="220"
          />
          <path
            className="analytics-main-line-live"
            d="M38 198C68 144 88 164 118 150C150 135 152 104 185 113C218 122 204 168 238 156C270 143 282 97 322 111C360 124 367 83 404 88C437 92 437 128 478 72"
            pathLength="220"
          />
          {[118, 185, 238, 322, 404].map((x, index) => (
            <circle
              key={x}
              className="analytics-chart-point"
              cx={x}
              cy={[150, 113, 156, 111, 88][index]}
              r="4.5"
            />
          ))}
        </g>

        <g data-analytics-regional-bars transform="translate(634 214)">
          <rect className="analytics-card" width="228" height="154" rx="18" />
          <text className="analytics-label" x="18" y="30">{labels.regionPerformance}</text>
          <g className="analytics-chart-grid small">
            {[0, 1, 2].map((line) => (
              <path key={line} d={`M34 ${70 + line * 24}H194`} />
            ))}
          </g>
          <g transform="translate(48 128)">
            {regionalBars.map((height, index) => (
              <g key={height} className={`analytics-bar analytics-bar-${index + 1}`}>
                <rect x={index * 30} y={-height} width="15" height={height} rx="5" />
              </g>
            ))}
          </g>
        </g>

        <g data-analytics-secondary-chart transform="translate(634 386)">
          <rect className="analytics-card" width="228" height="162" rx="18" />
          <text className="analytics-label" x="18" y="30">{labels.responseTime}</text>
          <text className="analytics-response-value" x="18" y="60">1u 12m</text>
          <path className="analytics-sla-threshold vertical" d="M156 48V134" />
          <text className="analytics-tiny-label" x="164" y="61">SLA</text>
          {[
            { y: 82, label: "Mail", width: 74, className: "good" },
            { y: 106, label: "Chat", width: 124, className: "risk" },
            { y: 130, label: labels.phone, width: 96, className: "good" },
          ].map((row) => (
            <g key={row.label} className="analytics-response-row">
              <text className="analytics-tiny-label" x="18" y={row.y + 4}>{row.label}</text>
              <path className="analytics-response-track" d={`M62 ${row.y}H194`} />
              <path
                className={`analytics-response-live ${row.className}`}
                d={`M62 ${row.y}H${62 + row.width}`}
                pathLength="100"
              />
              <circle className={`analytics-response-dot ${row.className}`} cx={62 + row.width} cy={row.y} r="4" />
            </g>
          ))}
        </g>

        <g data-analytics-source-health transform="translate(104 472)">
          <rect className="analytics-card" width="154" height="82" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.sources}</text>
          <g transform="translate(18 39)">
            {[0, 1, 2].map((index) => (
              <g key={index} transform={`translate(${index * 39} 0)`} className={`analytics-source-node source-${index + 1}`}>
                <rect className="analytics-source-tile" width="29" height="27" rx="7" />
                {index === 0 ? <path className="analytics-source-icon" d="M8 8C8 5 21 5 21 8V19C21 22 8 22 8 19ZM8 8C8 11 21 11 21 8M8 14C8 17 21 17 21 14" /> : null}
                {index === 1 ? <path className="analytics-source-icon" d="M8 8H21V20H8ZM12 8V20M17 8V20M8 14H21" /> : null}
                {index === 2 ? <text className="analytics-source-api" x="14.5" y="17" textAnchor="middle">API</text> : null}
                <circle className={index === 1 ? "analytics-source-status muted" : "analytics-source-status"} cx="32" cy="4" r="2.3" />
              </g>
            ))}
          </g>
        </g>

        <g data-analytics-conversion-gauge transform="translate(280 472)">
          <rect className="analytics-card" width="154" height="82" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.conversion}</text>
          <path className="analytics-gauge-track" d="M48 67A29 29 0 0 1 106 67" />
          <path className="analytics-gauge-live" d="M48 67A29 29 0 0 1 106 67" pathLength="100" />
          <g data-analytics-conversion-needle className="analytics-gauge-needle-wrap">
            <path className="analytics-gauge-needle" d="M77 67L99 51" />
          </g>
          <circle className="analytics-gauge-dot" cx="77" cy="67" r="5" />
        </g>

        <g data-analytics-alert-summary transform="translate(456 472)">
          <rect className="analytics-card" width="154" height="82" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.alerts}</text>
          {[0, 1, 2].map((row) => (
            <g key={row} transform={`translate(18 ${36 + row * 14})`}>
              <rect className="analytics-alert-icon" width="8" height="8" rx="2" />
              <path className={row === 0 ? "analytics-row strong" : "analytics-row"} d={`M18 4H${row === 0 ? 86 : 74}`} />
              {row === 0 ? <path className="analytics-alert-check" d="M111 1L115 5L123 -5" /> : null}
            </g>
          ))}
        </g>

        <g data-analytics-margin-card transform="translate(104 574)">
          <rect className="analytics-card" width="154" height="80" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.margin}</text>
          <text className="analytics-lower-value" x="16" y="54">32%</text>
          <g transform="translate(88 60)">
            {[25, 38, 30, 43].map((height, index) => (
              <rect key={height} className={`analytics-margin-bar analytics-bar-${index + 1}`} x={index * 13} y={-height} width="8" height={height} rx="4" />
            ))}
          </g>
        </g>

        <g data-analytics-forecast-card transform="translate(280 574)">
          <rect className="analytics-card" width="154" height="80" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.forecast}</text>
          <path className="analytics-forecast-base" d="M18 58C38 50 48 54 64 43C80 32 94 40 108 30" />
          <path className="analytics-forecast-line" d="M18 58C38 50 48 54 64 43C80 32 94 40 108 30" pathLength="100" />
          <path className="analytics-forecast-future" d="M108 30C122 22 132 24 140 16" />
          <circle className="analytics-chart-point small" cx="108" cy="30" r="3.4" />
        </g>

        <g data-analytics-data-check-card transform="translate(456 574)">
          <rect className="analytics-card" width="154" height="80" rx="16" />
          <text className="analytics-label small" x="16" y="22">{labels.dataCheck}</text>
          {[0, 1, 2].map((row) => (
            <g key={row} transform={`translate(18 ${34 + row * 12})`}>
              <path className="analytics-data-check-icon" d="M0 4L4 8L11 0" />
              <path className={row === 0 ? "analytics-row strong" : "analytics-row"} d={`M22 4H${row === 0 ? 92 : 78}`} />
            </g>
          ))}
        </g>

        <g data-analytics-cashflow-card transform="translate(634 574)">
          <rect className="analytics-card" width="228" height="80" rx="16" />
          <text className="analytics-label small" x="18" y="22">{labels.cashflow}</text>
          <path className="analytics-cashflow-base" d="M22 54H196" />
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <g key={index} className={`analytics-cashflow-dot cashflow-${index + 1}`}>
              <circle cx={36 + index * 28} cy={[52, 44, 56, 38, 46, 32][index]} r="4" />
            </g>
          ))}
          <path className="analytics-cashflow-line" d="M36 52L64 44L92 56L120 38L148 46L176 32" pathLength="100" />
        </g>
      </svg>
    </AnalyticsHeroFrame>
  );
}

export function AnalyticsFocusVisual({ variant }) {
  return (
    <AnalyticsMiniFrame data-analytics-focus-visual={variant} aria-hidden="true">
      <svg viewBox="0 0 360 126" focusable="false">
        <rect className="analytics-mini-bg" x="1" y="1" width="358" height="124" rx="24" />
        {variant === "definitions" ? <DefinitionsMini /> : null}
        {variant === "overview" ? <OverviewMini /> : null}
        {variant === "signals" ? <SignalsMini /> : null}
      </svg>
    </AnalyticsMiniFrame>
  );
}

export function AnalyticsMethodVisual({ variant }) {
  return (
    <AnalyticsMethodFrame data-analytics-method-visual={variant} aria-hidden="true">
      <svg viewBox="0 0 240 118" focusable="false">
        <rect className="analytics-method-bg" x="1" y="1" width="238" height="116" rx="22" />
        {variant === "sources" ? <SourcesMethod /> : null}
        {variant === "model" ? <ModelMethod /> : null}
        {variant === "alerts" ? <AlertsMethod /> : null}
        {variant === "quality" ? <QualityMethod /> : null}
      </svg>
    </AnalyticsMethodFrame>
  );
}

export function AnalyticsProofVisual({
  className,
  title = "Analytics case visual",
  ...frameProps
}) {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const labels = {
    rhythm: isNl ? "STUURRITME" : "OPERATING RHYTHM",
    truth: isNl ? "1 waarheid" : "1 source",
    cockpit: "COCKPIT",
    action: isNl ? "ACTIE" : "ACTION",
    rhythmCards: isNl
      ? ["WEEKSTART", "DAGSTURING", "BESLUIT"]
      : ["WEEK START", "DAY CONTROL", "DECISION"],
  };

  return (
    <AnalyticsProofFrame
      {...stripMediaSlotProps(frameProps)}
      className={className}
      data-analytics-proof-visual
      role="img"
      aria-label={title}
    >
      <svg viewBox="0 0 720 430" focusable="false">
        <g data-analytics-proof-system transform="translate(22 58)">
          <text className="analytics-proof-label" x="8" y="0">{labels.rhythm}</text>

          <g data-analytics-proof-truth transform="translate(0 46)">
            <rect className="analytics-proof-card truth-card" width="214" height="164" rx="24" />
            <text className="analytics-proof-value" x="24" y="48">{labels.truth}</text>
            <g transform="translate(24 78)">
              {["CRM", "ERP", "SERVICE"].map((label, index) => (
                <g key={label} transform={`translate(0 ${index * 22})`}>
                  <rect className="analytics-proof-source-pill" width="118" height="14" rx="7" />
                  <circle className={`analytics-proof-source-dot dot-${index + 1}`} cx="8" cy="7" r="2.5" />
                  <text className="analytics-proof-tiny" x="18" y="10">{label}</text>
                </g>
              ))}
            </g>
          </g>

          <path className="analytics-proof-static-link" d="M222 130H250" />

          <g data-analytics-proof-cockpit transform="translate(250 24)">
            <rect className="analytics-proof-cockpit" width="270" height="236" rx="28" />
            <text className="analytics-proof-label small" x="24" y="34">{labels.cockpit}</text>
            <g transform="translate(24 54)">
              {[
                { label: "MT", x: 0, active: true },
                { label: "OPS", x: 54 },
                { label: "SALES", x: 108 },
              ].map((item) => (
                <g key={item.label} transform={`translate(${item.x} 0)`}>
                  <rect className={item.active ? "analytics-proof-tab active" : "analytics-proof-tab"} width="46" height="24" rx="9" />
                  <text className="analytics-proof-tiny centered" x="23" y="16">{item.label}</text>
                </g>
              ))}
            </g>
            <g transform="translate(24 94)">
              <rect className="analytics-proof-metric" width="92" height="64" rx="14" />
              <text className="analytics-proof-tiny metric-label" x="18" y="21">KPI</text>
              <path className="analytics-proof-row strong" d="M18 34H58" />
              <path className="analytics-proof-row" d="M18 46H48" />
              <path className="analytics-proof-kpi-check" d="M66 42L72 48L84 30" />
              <rect className="analytics-proof-metric" x="106" width="116" height="64" rx="14" />
              <text className="analytics-proof-tiny metric-label" x="128" y="21">TEAM</text>
              {[0, 1, 2, 3].map((index) => (
                <circle
                  key={index}
                  className={index === 1 ? "analytics-proof-role-dot active" : "analytics-proof-role-dot"}
                  cx={130 + index * 22}
                  cy="42"
                  r="7"
                />
              ))}
              <path className="analytics-proof-row" d="M128 54H198" />
            </g>
            <g transform="translate(24 176)">
              <rect className="analytics-proof-action-strip" width="222" height="34" rx="11" />
              <path className="analytics-proof-row strong" d="M16 13H88" />
              <path className="analytics-proof-row" d="M104 13H164" />
              <circle className="analytics-proof-live-dot" cx="194" cy="13" r="4" />
              <path className="analytics-proof-row faint" d="M16 24H58M78 24H126M146 24H182" />
            </g>
          </g>

          <path className="analytics-proof-static-link" d="M520 130H558" />

          <g data-analytics-proof-owner transform="translate(558 70)">
            <rect className="analytics-proof-card owner-card" width="118" height="148" rx="24" />
            <text className="analytics-proof-label small" x="22" y="32">{labels.action}</text>
            <circle className="analytics-proof-owner-head" cx="34" cy="62" r="12" />
            <path className="analytics-proof-owner-body" d="M16 94C21 75 47 75 52 94" />
            <g transform="translate(70 54)">
              {[0, 1, 2].map((row) => (
                <g key={row} transform={`translate(0 ${row * 19})`}>
                  <path className={row === 0 ? "analytics-proof-check active" : "analytics-proof-check"} d="M0 6L4 10L12 0" />
                  <path className="analytics-proof-row" d="M20 5H36" />
                </g>
              ))}
            </g>
          </g>

          <g data-analytics-proof-rhythm transform="translate(74 306)">
            {labels.rhythmCards.map((label, index) => (
              <g key={label} transform={`translate(${index * 184} 0)`}>
                <rect className="analytics-proof-rhythm-card" width="152" height="46" rx="15" />
                <circle className={`analytics-proof-rhythm-dot dot-${index + 1}`} cx="22" cy="23" r="4.5" />
                <text className="analytics-proof-tiny rhythm-label" x="42" y="27">{label}</text>
              </g>
            ))}
          </g>
        </g>
      </svg>
    </AnalyticsProofFrame>
  );
}

export { analyticsFocusVisuals, analyticsMethodVisuals };

function DefinitionsMini() {
  return (
    <>
      {[24, 52, 80].map((y, index) => (
        <g key={y} transform={`translate(28 ${y})`}>
          <rect className={index === 1 ? "analytics-source selected" : "analytics-source"} width="82" height="20" rx="8" />
          <circle className="analytics-dot" cx="12" cy="11" r="4" />
          <path className="analytics-mini-row" d="M26 8H62M26 15H49" />
        </g>
      ))}
      <g data-analytics-definition-core transform="translate(138 20)">
        <rect className="analytics-definition-card" width="92" height="86" rx="18" />
        <text className="analytics-kpi-text" x="46" y="30" textAnchor="middle">KPI</text>
        <path className="analytics-definition-rule" d="M24 47H68M31 59H61M38 71H54" />
        <path className="analytics-definition-divider" d="M30 53H62" />
      </g>
      <g transform="translate(266 35)">
        <rect className="analytics-validated-card" width="54" height="54" rx="16" />
        <path className="analytics-definition-check" d="M13 29L24 40L43 16" />
      </g>
    </>
  );
}

function OverviewMini() {
  return (
    <>
      <g transform="translate(24 24)">
        {[0, 1, 2].map((index) => (
          <g key={index} transform={`translate(0 ${index * 27})`}>
            <rect className="analytics-export-chip" width="72" height="20" rx="8" />
            <path className="analytics-export-icon" d="M12 6H20V14H12ZM28 7H58M28 13H48" />
          </g>
        ))}
      </g>
      <g data-analytics-team-overview-board transform="translate(116 18)">
        <rect className="analytics-overview-card wide" width="140" height="90" rx="18" />
        <path className="analytics-mini-row strong" d="M18 20H76" />
        <g transform="translate(18 38)">
          {[0, 1, 2].map((row) => (
            <g key={row} className={`analytics-table-row table-row-${row + 1}`} transform={`translate(0 ${row * 15})`}>
              <path className="analytics-mini-row" d="M0 0H78" />
              <path className={`analytics-overview-scan overview-${row + 1}`} d="M0 0H78" />
              <circle className="analytics-dot overview-status" cx="98" cy="0" r="3.5" />
            </g>
          ))}
        </g>
      </g>
      <g data-analytics-team-cluster transform="translate(274 27)">
        {[
          [0, 0],
          [24, 0],
          [48, 0],
          [12, 30],
          [36, 30],
        ].map(([x, y], index) => (
          <g key={`${x}-${y}`} className={`analytics-team-dot team-${index + 1}`} transform={`translate(${x} ${y})`}>
            <circle className="analytics-human-head" cx="9" cy="8" r="6" />
            <path className="analytics-human-body" d="M1 23C3 15 15 15 17 23" />
          </g>
        ))}
      </g>
    </>
  );
}

function SignalsMini() {
  return (
    <>
      <g transform="translate(28 22)">
        <rect className="analytics-signal-chart" width="178" height="82" rx="18" />
        <path className="analytics-threshold" d="M20 35H156" />
        <path className="analytics-signal-area" d="M20 70C42 62 52 64 72 55C94 45 101 35 123 35C137 35 145 28 156 18V74H20Z" />
        <path className="analytics-signal-line synced" d="M20 70C42 62 52 64 72 55C94 45 101 35 123 35C137 35 145 28 156 18" pathLength="100" />
        <circle data-analytics-signal-hit className="analytics-signal-hit synced" cx="123" cy="35" r="6" />
      </g>
      <g data-analytics-signal-alert transform="translate(226 37)">
        <rect className="analytics-alert-card signal-active" width="102" height="52" rx="16" />
        <g transform="translate(15 9)">
          <g className="analytics-alarm-icon synced">
            <path className="analytics-bell-wave" d="M3 15C1 18 1 22 3 25M34 15C36 18 36 22 34 25" />
            <path className="analytics-bell-body" d="M8 27H29L25 22V17C25 12 21 10 18.5 10C16 10 12 12 12 17V22L8 27Z" />
            <path className="analytics-bell-clapper" d="M15 31C17 33 20 33 22 31" />
            <circle className="analytics-bell-knob" cx="18.5" cy="9" r="2" />
          </g>
        </g>
        <path className="analytics-mini-row strong" d="M64 19H89" />
        <path className="analytics-mini-row" d="M64 32H82" />
      </g>
    </>
  );
}

function SourcesMethod() {
  return (
    <>
      <g data-analytics-method-sources transform="translate(18 29)">
        {[
          { icon: "db", x: 0, y: 0 },
          { icon: "table", x: 0, y: 34 },
          { icon: "api", x: 40, y: 17 },
        ].map((source, index) => (
          <g key={source.icon} className={`analytics-method-source source-${index + 1}`} transform={`translate(${source.x} ${source.y})`}>
            <rect className="analytics-method-source-tile" width="30" height="28" rx="8" />
            {source.icon === "db" ? <path className="analytics-method-source-icon" d="M8 8C8 5 22 5 22 8V19C22 22 8 22 8 19ZM8 8C8 11 22 11 22 8M8 14C8 17 22 17 22 14" /> : null}
            {source.icon === "table" ? <path className="analytics-method-source-icon" d="M8 8H22V20H8ZM12.7 8V20M17.3 8V20M8 14H22" /> : null}
            {source.icon === "api" ? <text className="analytics-method-api" x="15" y="17" textAnchor="middle">API</text> : null}
            <circle className="analytics-method-status" cx="24.8" cy="5.2" r="1.8" />
          </g>
        ))}
      </g>
      <g data-analytics-method-definition-card transform="translate(96 23)">
        <rect className="analytics-method-focus-card" width="74" height="72" rx="17" />
        <text className="analytics-kpi-text small" x="37" y="27" textAnchor="middle">KPI</text>
        <path className="analytics-method-rule strong" d="M22 43H52" />
        <path className="analytics-method-rule" d="M26 54H48" />
      </g>
      <g transform="translate(184 37)">
        <rect className="analytics-method-confirm-card" width="42" height="42" rx="14" />
        <path className="analytics-definition-check tiny" d="M10 23L18 31L32 12" />
      </g>
    </>
  );
}

function ModelMethod() {
  return (
    <>
      <g data-analytics-method-raw-data transform="translate(20 33)">
        {[0, 1, 2, 3].map((index) => (
          <rect
            key={index}
            className={`analytics-method-data-block block-${index + 1}`}
            x={(index % 2) * 27}
            y={Math.floor(index / 2) * 27}
            width="17"
            height="17"
            rx="5"
          />
        ))}
      </g>
      <g data-analytics-method-model-core transform="translate(88 22)">
        <rect className="analytics-method-focus-card" width="72" height="74" rx="18" />
        <text className="analytics-method-model-label" x="36" y="25" textAnchor="middle">MODEL</text>
        <g transform="translate(18 36)">
          <circle className="analytics-model-node main" cx="18" cy="13" r="5.3" />
          <circle className="analytics-model-node" cx="6" cy="3" r="3.4" />
          <circle className="analytics-model-node" cx="31" cy="3" r="3.4" />
          <circle className="analytics-model-node" cx="6" cy="25" r="3.4" />
          <circle className="analytics-model-node" cx="31" cy="25" r="3.4" />
          <path className="analytics-model-structure" d="M18 13L6 3M18 13L31 3M18 13L6 25M18 13L31 25" />
        </g>
      </g>
      <g data-analytics-method-audiences transform="translate(178 27)">
        {[0, 1].map((index) => (
          <g key={index} transform={`translate(0 ${index * 35})`}>
            <rect className="analytics-method-chip" width="46" height="26" rx="9" />
            <circle className="analytics-method-person-head" cx="11" cy="9" r="4" />
            <path className="analytics-method-person-body" d="M4.5 20C6.5 14 15.5 14 17.5 20" />
            <path className="analytics-method-rule short" d="M25 9H37M25 17H34" />
          </g>
        ))}
      </g>
    </>
  );
}

function AlertsMethod() {
  return (
    <>
      <g data-analytics-method-schedule transform="translate(21 36)">
        <rect className="analytics-method-chip" width="50" height="46" rx="14" />
        <circle className="analytics-method-clock" cx="25" cy="23" r="12" />
        <path className="analytics-method-clock-hand" d="M25 15V24L32 28" />
        <circle className="analytics-method-status" cx="42.5" cy="8" r="2.2" />
      </g>
      <g data-analytics-method-alert-bell transform="translate(96 28)">
        <rect className="analytics-alert-card method-alert" width="58" height="58" rx="17" />
        <g transform="translate(11 8)">
          <g className="analytics-method-bell-shake">
            <path className="analytics-method-bell-wave" d="M3 14C1.5 17 1.5 22 3 25M33 14C34.5 17 34.5 22 33 25" />
            <path className="analytics-method-bell" d="M8 27H28L24.5 23V17C24.5 12 21 10 18 10C15 10 11.5 12 11.5 17V23L8 27Z" />
            <path className="analytics-bell-clapper" d="M15 31C17 33 19 33 21 31" />
            <circle className="analytics-bell-knob" cx="18" cy="9" r="1.8" />
          </g>
        </g>
      </g>
      <g data-analytics-method-distribution transform="translate(174 34)">
        <rect className="analytics-method-chip" width="48" height="50" rx="14" />
        <path className="analytics-method-inbox" d="M13 18H35V32H13ZM13 18L24 26L35 18" />
        <path className="analytics-method-rule short" d="M15 39H33" />
      </g>
    </>
  );
}

function QualityMethod() {
  return (
    <>
      <g data-analytics-method-quality-card transform="translate(19 34)">
        <rect className="analytics-method-chip" width="54" height="50" rx="14" />
        {[13, 25, 37].map((y, index) => (
          <path key={y} className={index === 1 ? "analytics-method-rule strong" : "analytics-method-rule"} d={`M13 ${y}H${index === 1 ? 42 : 35}`} />
        ))}
      </g>
      <g data-analytics-method-eye transform="translate(88 26)">
        <path className="analytics-method-eye-outline" d="M8 33C24 10 58 10 74 33C58 56 24 56 8 33Z" />
        <path className="analytics-method-eye-orbit" d="M8 33C24 10 58 10 74 33C58 56 24 56 8 33Z" pathLength="100" />
        <g className="analytics-method-eye-blink">
          <circle className="analytics-method-eye-pupil" cx="41" cy="33" r="8" />
        </g>
      </g>
      <g transform="translate(184 34)">
        <rect className="analytics-method-card" width="38" height="50" rx="12" />
        <path className="analytics-definition-check small" d="M9 26L16 33L29 16" />
      </g>
    </>
  );
}

const drawLine = keyframes`
  from {
    stroke-dashoffset: 100;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const loopLine = keyframes`
  to {
    stroke-dashoffset: -120;
  }
`;

const eyeOrbitLoop = keyframes`
  to {
    stroke-dashoffset: -100;
  }
`;

const barRise = keyframes`
  0%, 100% {
    transform: scaleY(0.64);
    opacity: 0.72;
  }
  45%, 62% {
    transform: scaleY(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.62;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }
  50% {
    opacity: 1;
    filter: drop-shadow(0 0 9px rgba(123, 210, 236, 0.42));
  }
`;

const gauge = keyframes`
  0%, 100% {
    transform: rotate(-22deg);
  }
  55% {
    transform: rotate(8deg);
  }
`;

const signalHitPulse = keyframes`
  0%, 79.8%, 100% {
    opacity: 0.16;
    transform: scale(0.68);
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }
  82%, 90% {
    opacity: 1;
    transform: scale(1.12);
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.56));
  }
`;

const signalAlertGlow = keyframes`
  0%, 82%, 100% {
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
    stroke-opacity: 0.36;
  }
  85%, 94% {
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.34));
    stroke-opacity: 0.78;
  }
`;

const signalAlarmShake = keyframes`
  0%, 83%, 100% {
    transform: translateX(0) rotate(0deg);
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }
  86% {
    transform: translateX(-1.2px) rotate(-2deg);
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.42));
  }
  89% {
    transform: translateX(1.2px) rotate(2deg);
  }
  92% {
    transform: translateX(-0.8px) rotate(-1.1deg);
  }
  95% {
    transform: translateX(0) rotate(0deg);
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.32));
  }
`;

const methodBellShake = keyframes`
  0%, 72%, 100% {
    transform: rotate(0deg);
  }
  76% {
    transform: rotate(-2.2deg);
  }
  80% {
    transform: rotate(2.2deg);
  }
  84% {
    transform: rotate(-1deg);
  }
  88% {
    transform: rotate(0deg);
  }
`;

const eyeBlink = keyframes`
  0%, 38%, 48%, 100% {
    transform: scaleY(1);
  }
  43% {
    transform: scaleY(0.16);
  }
`;

const proofCardGlow = keyframes`
  0%, 100% {
    opacity: 0.7;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }
  50% {
    opacity: 1;
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.28));
  }
`;

const AnalyticsHeroFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 920 / 720;
  overflow: hidden;
  border-radius: 24px;
  background:
    radial-gradient(circle at 28% 18%, rgba(123, 210, 236, 0.13), transparent 34%),
    radial-gradient(circle at 88% 82%, rgba(205, 235, 243, 0.1), transparent 36%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.012));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .analytics-shell,
  .analytics-card,
  .analytics-side-rail,
  .analytics-search {
    fill: url(#analytics-card-fill);
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 1.4;
  }

  .analytics-shell {
    fill: rgba(255, 255, 255, 0.018);
    stroke: rgba(255, 255, 255, 0.2);
  }

  .analytics-side-rail {
    fill: rgba(255, 255, 255, 0.026);
  }

  .analytics-glass-line,
  .analytics-search-handle,
  .analytics-chart-grid path,
  .analytics-row,
  .analytics-mini-line-base,
  .analytics-main-line-base,
  .analytics-gauge-track,
  .analytics-proof-trend-base {
    fill: none;
    stroke: rgba(170, 195, 202, 0.24);
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-glass-line {
    stroke-width: 1;
  }

  .analytics-chart-grid path {
    stroke-width: 1;
  }

  .analytics-chart-grid.small path {
    opacity: 0.72;
  }

  .analytics-header-title,
  .analytics-label,
  .analytics-header-muted,
  .analytics-tiny-label {
    fill: rgba(255, 255, 255, 0.76);
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  .analytics-label {
    font-size: 12px;
    letter-spacing: 0.09em;
  }

  .analytics-label.small {
    font-size: 10px;
  }

  .analytics-tiny-label {
    fill: rgba(255, 255, 255, 0.44);
    font-size: 8px;
    letter-spacing: 0.06em;
  }

  .analytics-header-muted {
    fill: rgba(255, 255, 255, 0.42);
    font-weight: 500;
    letter-spacing: 0;
  }

  .analytics-value {
    fill: rgba(245, 251, 252, 0.95);
    font-size: 36px;
    font-weight: 800;
    letter-spacing: 0;
  }

  .analytics-value.compact {
    font-size: 30px;
  }

  .analytics-response-value,
  .analytics-lower-value {
    fill: rgba(245, 251, 252, 0.92);
    font-size: 22px;
    font-weight: 850;
    letter-spacing: 0;
  }

  .analytics-lower-value {
    font-size: 24px;
  }

  .analytics-change {
    fill: rgba(162, 235, 219, 0.92);
    font-size: 13px;
    font-weight: 800;
  }

  .analytics-search-dot,
  .analytics-gauge-dot,
  .analytics-chart-point,
  .analytics-response-dot {
    fill: rgba(205, 235, 243, 0.82);
  }

  .analytics-user {
    fill: rgba(123, 210, 236, 0.32);
    stroke: rgba(205, 235, 243, 0.72);
  }

  .analytics-user-glow {
    fill: none;
    stroke: rgba(123, 210, 236, 0.28);
    animation: ${pulse} 3.6s ease-in-out infinite;
  }

  .analytics-rail-icon rect {
    fill: transparent;
    stroke: rgba(255, 255, 255, 0.2);
  }

  .analytics-rail-icon path {
    fill: none;
    stroke: rgba(255, 255, 255, 0.38);
    stroke-width: 1.6;
    stroke-linecap: round;
  }

  .analytics-rail-icon.active rect {
    fill: rgba(123, 210, 236, 0.14);
    stroke: rgba(205, 235, 243, 0.46);
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.28));
  }

  .analytics-mini-area,
  .analytics-main-area,
  .analytics-small-chart-area {
    fill: url(#analytics-area-gradient);
  }

  .analytics-mini-line-base,
  .analytics-main-line-base,
  .analytics-uptime-track,
  .analytics-response-track,
  .analytics-forecast-base,
  .analytics-cashflow-base,
  .analytics-proof-trend-base {
    stroke-width: 2;
  }

  .analytics-mini-line-live,
  .analytics-main-line-live,
  .analytics-dashboard-live,
  .analytics-signal-line,
  .analytics-small-chart-line,
  .analytics-uptime-live,
  .analytics-response-live,
  .analytics-forecast-line,
  .analytics-cashflow-line,
  .analytics-proof-trend-live,
  .analytics-proof-chart {
    fill: none;
    stroke: url(#analytics-line-gradient);
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.8;
    stroke-dasharray: 100;
    animation: ${drawLine} 4.2s ease-in-out infinite;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.25));
  }

  .analytics-main-line-live {
    stroke-width: 3.2;
    stroke-dasharray: 220;
    animation-duration: 5.8s;
  }

  .analytics-uptime-track {
    fill: none;
    stroke: rgba(170, 195, 202, 0.22);
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-uptime-live {
    stroke-width: 2.6;
    animation-duration: 4.8s;
  }

  .analytics-uptime-bar {
    fill: rgba(123, 210, 236, 0.24);
    animation: ${pulse} 4s ease-in-out infinite;
  }

  .analytics-uptime-bar.downtime {
    fill: rgba(205, 235, 243, 0.18);
    animation-delay: 0.42s;
  }

  .analytics-chart-point {
    stroke: rgba(12, 23, 26, 0.82);
    stroke-width: 2;
    animation: ${pulse} 4.1s ease-in-out infinite;
  }

  .analytics-chart-point.small {
    stroke-width: 1.5;
  }

  .analytics-bar rect,
  .analytics-margin-bar,
  .analytics-proof-bar {
    fill: rgba(205, 235, 243, 0.72);
    transform-box: fill-box;
    transform-origin: center bottom;
    animation: ${barRise} 4.2s ease-in-out infinite;
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.2));
  }

  .analytics-bar-2 {
    animation-delay: 0.22s;
  }

  .analytics-bar-3 {
    animation-delay: 0.44s;
  }

  .analytics-bar-4 {
    animation-delay: 0.66s;
  }

  .analytics-bar-5 {
    animation-delay: 0.88s;
  }

  .analytics-bar-6 {
    animation-delay: 1.1s;
  }

  .analytics-gauge-live {
    fill: none;
    stroke: rgba(123, 210, 236, 0.72);
    stroke-width: 7;
    stroke-linecap: round;
    stroke-dasharray: 74 100;
  }

  .analytics-gauge-track {
    stroke-width: 7;
  }

  .analytics-gauge-needle-wrap {
    transform-box: view-box;
    transform-origin: 77px 67px;
    animation: ${gauge} 4.8s ease-in-out infinite;
  }

  .analytics-gauge-needle {
    fill: none;
    stroke: rgba(225, 245, 250, 0.78);
    stroke-width: 3.8;
    stroke-linecap: round;
  }

  .analytics-response-track,
  .analytics-forecast-base,
  .analytics-cashflow-base {
    fill: none;
    stroke: rgba(170, 195, 202, 0.2);
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-response-live {
    stroke-width: 4.2;
    stroke-dasharray: 100;
    animation-duration: 3.8s;
  }

  .analytics-response-live.risk,
  .analytics-response-dot.risk {
    stroke: rgba(205, 235, 243, 0.62);
    fill: rgba(205, 235, 243, 0.62);
  }

  .analytics-response-live.good,
  .analytics-response-dot.good {
    stroke: rgba(123, 210, 236, 0.82);
    fill: rgba(123, 210, 236, 0.82);
  }

  .analytics-source-tile {
    fill: rgba(123, 210, 236, 0.08);
    stroke: rgba(205, 235, 243, 0.24);
    stroke-width: 1.2;
  }

  .analytics-source-link,
  .analytics-alert-check {
    fill: none;
    stroke: rgba(205, 235, 243, 0.72);
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-source-link {
    stroke-width: 2.1;
  }

  .analytics-source-icon {
    fill: none;
    stroke: rgba(205, 235, 243, 0.68);
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-source-api {
    fill: rgba(205, 235, 243, 0.74);
    font-size: 8px;
    font-weight: 900;
    letter-spacing: 0.04em;
  }

  .analytics-source-status {
    fill: rgba(162, 235, 219, 0.9);
    animation: ${pulse} 3.4s ease-in-out infinite;
  }

  .analytics-sla-threshold {
    fill: none;
    stroke: rgba(162, 235, 219, 0.24);
    stroke-width: 1.4;
    stroke-dasharray: 8 8;
  }

  .analytics-sla-threshold.vertical {
    stroke: rgba(162, 235, 219, 0.3);
  }

  .analytics-source-status.muted {
    fill: rgba(170, 195, 202, 0.48);
    animation-delay: 0.34s;
  }

  .analytics-alert-icon {
    fill: rgba(123, 210, 236, 0.18);
    stroke: rgba(205, 235, 243, 0.48);
  }

  .analytics-row {
    stroke-width: 3.2;
  }

  .analytics-row.strong {
    stroke: rgba(205, 235, 243, 0.68);
  }

  .analytics-alert-check {
    stroke: rgba(162, 235, 219, 0.9);
    stroke-width: 2.5;
    animation: ${pulse} 3s ease-in-out infinite;
  }

  .analytics-forecast-line,
  .analytics-cashflow-line {
    stroke-width: 2.6;
  }

  .analytics-forecast-future {
    fill: none;
    stroke: rgba(205, 235, 243, 0.45);
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-dasharray: 5 6;
  }

  .analytics-data-check-icon {
    fill: none;
    stroke: rgba(162, 235, 219, 0.86);
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-cashflow-dot circle {
    fill: rgba(123, 210, 236, 0.34);
    stroke: rgba(205, 235, 243, 0.46);
    animation: ${pulse} 3.8s ease-in-out infinite;
  }

  .cashflow-2 circle {
    animation-delay: 0.18s;
  }

  .cashflow-3 circle {
    animation-delay: 0.36s;
  }

  .cashflow-4 circle {
    animation-delay: 0.54s;
  }

  .cashflow-5 circle {
    animation-delay: 0.72s;
  }

  .cashflow-6 circle {
    animation-delay: 0.9s;
  }

  .analytics-alert-pulse {
    fill: rgba(162, 235, 219, 0.92);
    animation: ${pulse} 2.6s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .analytics-mini-line-live,
    .analytics-main-line-live,
    .analytics-dashboard-live,
    .analytics-signal-line,
    .analytics-small-chart-line,
    .analytics-uptime-live,
    .analytics-response-live,
    .analytics-forecast-line,
    .analytics-cashflow-line,
    .analytics-proof-trend-live,
    .analytics-proof-chart,
    .analytics-uptime-bar,
    .analytics-bar rect,
    .analytics-margin-bar,
    .analytics-proof-bar,
    .analytics-gauge-needle-wrap,
    .analytics-source-status,
    .analytics-alert-check,
    .analytics-cashflow-dot circle,
    .analytics-alert-pulse,
    .analytics-user-glow,
    .analytics-chart-point {
      animation: none !important;
      opacity: 1;
    }
  }
`;

const AnalyticsMiniFrame = styled.div`
  width: 100%;
  aspect-ratio: 360 / 126;
  overflow: hidden;
  border-radius: 20px;
  margin-bottom: clamp(4px, 0.8vw, 8px);
  background:
    radial-gradient(circle at 50% 48%, rgba(123, 210, 236, 0.1), transparent 36%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.014));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .analytics-mini-bg,
  .analytics-source,
  .analytics-definition-card,
  .analytics-validated-card,
  .analytics-export-chip,
  .analytics-overview-card,
  .analytics-team-panel,
  .analytics-signal-chart,
  .analytics-alert-card,
  .analytics-method-chip,
  .analytics-method-card,
  .analytics-model-card {
    fill: rgba(255, 255, 255, 0.044);
    stroke: rgba(255, 255, 255, 0.18);
    stroke-width: 1.2;
  }

  .analytics-mini-bg {
    fill: rgba(255, 255, 255, 0.016);
    stroke: rgba(255, 255, 255, 0.11);
  }

  .selected,
  .analytics-definition-card,
  .analytics-overview-card.wide,
  .analytics-alert-card {
    fill: rgba(123, 210, 236, 0.078);
    stroke: rgba(205, 235, 243, 0.27);
  }

  .analytics-mini-row,
  .analytics-quiet-link,
  .analytics-definition-check,
  .analytics-definition-rule,
  .analytics-definition-divider,
  .analytics-export-icon,
  .analytics-dashboard-live,
  .analytics-threshold,
  .analytics-signal-line,
  .analytics-bell,
  .analytics-bell-body,
  .analytics-bell-wave,
  .analytics-bell-clapper,
  .analytics-human-body {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-mini-row {
    stroke: rgba(170, 195, 202, 0.58);
    stroke-width: 3;
  }

  .analytics-mini-row.strong {
    stroke: rgba(205, 235, 243, 0.86);
    stroke-width: 4;
  }

  .analytics-quiet-link {
    stroke: rgba(205, 235, 243, 0.34);
    stroke-width: 2;
  }

  .analytics-dot,
  .analytics-human-head {
    fill: rgba(205, 235, 243, 0.78);
  }

  .analytics-kpi-text {
    fill: rgba(225, 245, 250, 0.9);
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.02em;
  }

  .analytics-kpi-text.small {
    font-size: 15px;
  }

  .analytics-definition-check {
    stroke: rgba(225, 245, 250, 0.9);
    stroke-width: 5;
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.22));
  }

  .analytics-definition-check.small {
    stroke-width: 3.6;
  }

  .analytics-definition-rule {
    stroke: rgba(170, 195, 202, 0.52);
    stroke-width: 3;
  }

  .analytics-definition-divider {
    stroke: rgba(123, 210, 236, 0.52);
    stroke-width: 1.6;
  }

  .analytics-validated-card {
    fill: rgba(123, 210, 236, 0.06);
    stroke: rgba(205, 235, 243, 0.24);
  }

  .analytics-export-chip {
    fill: rgba(255, 255, 255, 0.026);
  }

  .analytics-export-icon {
    stroke: rgba(170, 195, 202, 0.62);
    stroke-width: 1.8;
  }

  .analytics-table-row {
    opacity: 0.82;
  }

  .analytics-overview-scan {
    fill: none;
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-dasharray: 18 78;
    animation: ${loopLine} 3.05s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.2));
  }

  .analytics-overview-scan.overview-2 {
    stroke: rgba(205, 235, 243, 0.86);
  }

  .analytics-overview-scan.overview-3 {
    stroke: rgba(123, 210, 236, 0.72);
  }

  .overview-status {
    opacity: 0.84;
  }

  .analytics-dashboard-area,
  .analytics-signal-area {
    fill: rgba(123, 210, 236, 0.18);
  }

  .analytics-dashboard-live,
  .analytics-signal-line {
    stroke: rgba(205, 235, 243, 0.86);
    stroke-width: 3;
    stroke-dasharray: 100;
    animation: ${drawLine} 4s ease-in-out infinite;
  }

  .analytics-signal-line.synced {
    animation: ${drawLine} 4.4s linear infinite;
  }

  .analytics-dashboard-live.small-chart,
  .analytics-signal-line.compact {
    stroke-width: 2.4;
  }

  .analytics-team-dot {
    opacity: 0.9;
  }

  .team-2 {
    animation-delay: 0.25s;
  }

  .team-3 {
    animation-delay: 0.5s;
  }

  .analytics-human-body {
    stroke: rgba(123, 210, 236, 0.72);
    stroke-width: 2;
  }

  .analytics-threshold {
    stroke: rgba(255, 255, 255, 0.2);
    stroke-width: 1.4;
    stroke-dasharray: 6 7;
  }

  .analytics-bell {
    stroke: rgba(205, 235, 243, 0.82);
    stroke-width: 2.4;
  }

  .analytics-bell-body {
    stroke: rgba(225, 245, 250, 0.88);
    stroke-width: 2.35;
    fill: rgba(205, 235, 243, 0.08);
  }

  .analytics-bell-wave {
    stroke: rgba(123, 210, 236, 0.62);
    stroke-width: 2;
  }

  .analytics-bell-clapper {
    stroke: rgba(225, 245, 250, 0.78);
    stroke-width: 2;
  }

  .analytics-bell-knob {
    fill: rgba(225, 245, 250, 0.82);
  }

  .analytics-signal-hit {
    fill: rgba(123, 210, 236, 0.86);
    stroke: rgba(225, 245, 250, 0.78);
    stroke-width: 2;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
    transform-box: fill-box;
    transform-origin: center;
  }

  .analytics-signal-hit.synced {
    animation: ${signalHitPulse} 4.4s linear infinite;
  }

  .analytics-alert-card.signal-active {
    fill: rgba(123, 210, 236, 0.07);
    stroke: rgba(205, 235, 243, 0.32);
    animation: ${signalAlertGlow} 4.4s linear infinite;
  }

  .analytics-alarm-icon.synced {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${signalAlarmShake} 4.4s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .analytics-dashboard-live,
    .analytics-signal-line,
    .analytics-team-dot,
    .analytics-alert-pulse,
    .analytics-overview-scan,
    .analytics-signal-hit,
    .analytics-alert-card.signal-active,
    .analytics-alarm-icon.synced {
      animation: none !important;
      opacity: 1;
    }
  }
`;

const AnalyticsMethodFrame = styled(AnalyticsMiniFrame)`
  aspect-ratio: 240 / 118;
  margin-bottom: clamp(6px, 1vw, 10px);

  .analytics-method-bg {
    fill: rgba(255, 255, 255, 0.016);
    stroke: rgba(255, 255, 255, 0.11);
    stroke-width: 1.2;
  }

  .analytics-method-source-tile,
  .analytics-method-focus-card,
  .analytics-method-confirm-card,
  .analytics-method-data-block,
  .analytics-alert-card.method-alert {
    fill: rgba(123, 210, 236, 0.065);
    stroke: rgba(205, 235, 243, 0.22);
    stroke-width: 1.2;
  }

  .analytics-method-focus-card,
  .analytics-alert-card.method-alert {
    fill: rgba(123, 210, 236, 0.085);
    stroke: rgba(205, 235, 243, 0.31);
  }

  .analytics-method-source-icon,
  .analytics-method-rule,
  .analytics-model-structure,
  .analytics-method-person-body,
  .analytics-method-clock,
  .analytics-method-clock-hand,
  .analytics-method-bell,
  .analytics-method-bell-wave,
  .analytics-method-inbox,
  .analytics-method-eye-outline,
  .analytics-method-eye-orbit {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-method-source-icon,
  .analytics-method-inbox,
  .analytics-method-clock,
  .analytics-method-clock-hand {
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 1.8;
  }

  .analytics-method-api {
    fill: rgba(205, 235, 243, 0.78);
    font-size: 7px;
    font-weight: 900;
    letter-spacing: 0.04em;
  }

  .analytics-method-model-label {
    fill: rgba(205, 235, 243, 0.78);
    font-size: 7.5px;
    font-weight: 900;
    letter-spacing: 0.08em;
  }

  .analytics-method-status {
    fill: rgba(162, 235, 219, 0.88);
    animation: ${pulse} 3.5s ease-in-out infinite;
  }

  .analytics-method-rule {
    stroke: rgba(170, 195, 202, 0.58);
    stroke-width: 2.4;
  }

  .analytics-method-rule.strong {
    stroke: rgba(205, 235, 243, 0.78);
  }

  .analytics-method-rule.short {
    stroke-width: 2.2;
  }

  .analytics-definition-check.tiny {
    stroke: rgba(225, 245, 250, 0.88);
    stroke-width: 3.2;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.24));
  }

  .analytics-method-data-block {
    fill: rgba(205, 235, 243, 0.64);
    animation: ${pulse} 4s ease-in-out infinite;
  }

  .block-2 {
    animation-delay: 0.18s;
  }

  .block-3 {
    animation-delay: 0.36s;
  }

  .block-4 {
    animation-delay: 0.54s;
  }

  .analytics-model-structure {
    stroke: rgba(205, 235, 243, 0.36);
    stroke-width: 1.5;
  }

  .analytics-model-node {
    fill: rgba(205, 235, 243, 0.62);
    animation: ${pulse} 4.2s ease-in-out infinite;
  }

  .analytics-model-node.main {
    fill: rgba(123, 210, 236, 0.86);
  }

  .analytics-method-person-head {
    fill: rgba(205, 235, 243, 0.74);
  }

  .analytics-method-person-body {
    stroke: rgba(123, 210, 236, 0.66);
    stroke-width: 1.6;
  }

  .analytics-method-bell {
    stroke: rgba(225, 245, 250, 0.84);
    stroke-width: 2.2;
    fill: rgba(205, 235, 243, 0.08);
  }

  .analytics-method-bell-wave {
    stroke: rgba(123, 210, 236, 0.62);
    stroke-width: 1.9;
  }

  .analytics-method-bell-shake {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${methodBellShake} 4.2s ease-in-out infinite;
  }

  .analytics-method-eye-outline {
    stroke: rgba(205, 235, 243, 0.42);
    stroke-width: 2.4;
    fill: rgba(123, 210, 236, 0.035);
  }

  .analytics-method-eye-orbit {
    stroke: rgba(205, 235, 243, 0.82);
    stroke-width: 2.7;
    stroke-dasharray: 18 82;
    stroke-dashoffset: 0;
    animation: ${eyeOrbitLoop} 3.4s linear infinite;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.3));
  }

  .analytics-method-eye-pupil {
    fill: rgba(205, 235, 243, 0.82);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.28));
  }

  .analytics-method-eye-blink {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${eyeBlink} 4.5s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .analytics-method-status,
    .analytics-method-data-block,
    .analytics-model-node,
    .analytics-method-bell-shake,
    .analytics-method-eye-blink,
    .analytics-method-eye-orbit {
      animation: none !important;
    }
  }
`;

const AnalyticsProofFrame = styled(AnalyticsHeroFrame)`
  aspect-ratio: 16 / 10;

  .analytics-proof-shell,
  .analytics-proof-card,
  .analytics-model-core,
  .analytics-proof-cockpit,
  .analytics-proof-metric,
  .analytics-proof-action-strip,
  .analytics-proof-tab,
  .analytics-proof-rhythm-card {
    fill: rgba(255, 255, 255, 0.026);
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 1.3;
  }

  .analytics-proof-cockpit,
  .truth-card,
  .owner-card {
    fill: rgba(123, 210, 236, 0.045);
    stroke: rgba(205, 235, 243, 0.22);
  }

  .analytics-proof-label {
    fill: rgba(255, 255, 255, 0.55);
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.14em;
  }

  .analytics-proof-label.small {
    font-size: 10px;
    letter-spacing: 0.16em;
  }

  .analytics-proof-value {
    fill: rgba(245, 251, 252, 0.94);
    font-size: 28px;
    font-weight: 900;
  }

  .analytics-proof-tiny {
    fill: rgba(215, 235, 240, 0.72);
    font-size: 7.5px;
    font-weight: 850;
    letter-spacing: 0.08em;
  }

  .analytics-proof-tiny.centered {
    text-anchor: middle;
  }

  .analytics-proof-line,
  .analytics-model-line,
  .analytics-proof-static-link,
  .analytics-proof-metric-icon,
  .analytics-proof-owner-body,
  .analytics-proof-kpi-check,
  .analytics-proof-check,
  .analytics-proof-row {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .analytics-proof-static-link {
    stroke: rgba(205, 235, 243, 0.34);
    stroke-width: 3;
  }

  .analytics-proof-source-pill {
    fill: rgba(255, 255, 255, 0.038);
    stroke: rgba(205, 235, 243, 0.14);
    stroke-width: 1;
  }

  .analytics-proof-source-dot,
  .analytics-proof-live-dot,
  .analytics-proof-rhythm-dot {
    fill: rgba(162, 235, 219, 0.88);
    animation: ${pulse} 3.4s ease-in-out infinite;
  }

  .dot-2 {
    animation-delay: 0.28s;
  }

  .dot-3 {
    animation-delay: 0.56s;
  }

  .analytics-proof-tab.active {
    fill: rgba(123, 210, 236, 0.14);
    stroke: rgba(205, 235, 243, 0.42);
    animation: ${proofCardGlow} 4.2s ease-in-out infinite;
  }

  .analytics-proof-metric {
    fill: rgba(255, 255, 255, 0.026);
  }

  .analytics-proof-metric-icon {
    stroke: rgba(205, 235, 243, 0.74);
    stroke-width: 3;
  }

  .analytics-proof-mini-bar {
    fill: rgba(205, 235, 243, 0.62);
    transform-box: fill-box;
    transform-origin: center bottom;
    animation: ${barRise} 4.8s ease-in-out infinite;
  }

  .analytics-proof-action-strip {
    fill: rgba(255, 255, 255, 0.022);
  }

  .analytics-proof-row {
    stroke: rgba(170, 195, 202, 0.54);
    stroke-width: 2.4;
  }

  .analytics-proof-row.strong {
    stroke: rgba(205, 235, 243, 0.78);
  }

  .analytics-proof-row.faint {
    stroke: rgba(170, 195, 202, 0.28);
    stroke-width: 1.8;
  }

  .analytics-proof-rhythm-card {
    fill: rgba(123, 210, 236, 0.038);
    stroke: rgba(205, 235, 243, 0.14);
  }

  .analytics-proof-tiny.rhythm-label {
    fill: rgba(235, 246, 248, 0.76);
    font-size: 8.6px;
    letter-spacing: 0.16em;
  }

  .analytics-proof-tiny.metric-label {
    fill: rgba(235, 246, 248, 0.72);
    font-size: 8px;
    letter-spacing: 0.14em;
  }

  .analytics-proof-kpi-check {
    stroke: rgba(225, 245, 250, 0.88);
    stroke-width: 3;
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.25));
  }

  .analytics-proof-role-dot {
    fill: rgba(170, 195, 202, 0.42);
  }

  .analytics-proof-role-dot.active {
    fill: rgba(162, 235, 219, 0.84);
    animation: ${pulse} 5.8s ease-in-out infinite;
  }

  .analytics-proof-owner-head {
    fill: rgba(205, 235, 243, 0.76);
  }

  .analytics-proof-owner-body {
    stroke: rgba(123, 210, 236, 0.68);
    stroke-width: 3;
  }

  .analytics-proof-check {
    stroke: rgba(170, 195, 202, 0.52);
    stroke-width: 2.4;
  }

  .analytics-proof-check.active {
    stroke: rgba(225, 245, 250, 0.92);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.3));
    animation: ${proofCardGlow} 4.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .analytics-proof-source-dot,
    .analytics-proof-live-dot,
    .analytics-proof-rhythm-dot,
    .analytics-proof-role-dot.active,
    .analytics-proof-tab.active,
    .analytics-proof-mini-bar,
    .analytics-proof-check.active {
      animation: none !important;
    }
  }
`;
